import React, { forwardRef } from "react";
import { SnackbarContent } from "notistack";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

// Motivational snackbar shown when the candidate's profile completion increases.
// Intentionally styled to match the new candidate dashboard (not the default
// notistack look) so it feels like an encouraging in-product reward.
const GREEN = "hsl(153, 68%, 25%)";
const GREEN_TINT = "hsla(153, 68%, 25%, 0.1)";
const DARK_TEAL = "hsl(178, 93%, 10%)";
const MUTED = "hsl(178, 10%, 45%)";
const TRACK = "hsla(160, 10%, 90%, 0.9)";

const ProfileBoostSnackbar = forwardRef(
  ({ id, title, subtitle, percentage = 0, delta = 0 }, ref) => {
    const pct = Math.max(0, Math.min(100, Math.round(percentage)));

    return (
      <SnackbarContent ref={ref} role="status" data-snackbar-id={id}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          sx={{
            width: { xs: "calc(100vw - 32px)", sm: "360px" },
            maxWidth: "360px",
            backgroundColor: "hsl(0, 0%, 100%)",
            borderRadius: "16px",
            border: "1px solid hsla(160, 10%, 90%, 0.8)",
            boxShadow: "0px 8px 24px rgba(10, 37, 33, 0.14)",
            padding: "14px 16px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Icon badge */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                flexShrink: 0,
                backgroundColor: GREEN_TINT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Sparkles size={20} color={GREEN} />
            </Box>

            {/* Copy */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                  fontSize: "14px",
                  lineHeight: 1.2,
                  color: DARK_TEAL,
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    fontSize: "12px",
                    lineHeight: 1.3,
                    color: MUTED,
                    mt: "2px",
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>

            {/* Gain badge */}
            {delta > 0 && (
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: "flex-start",
                  backgroundColor: GREEN_TINT,
                  color: GREEN,
                  borderRadius: "9999px",
                  padding: "3px 10px",
                  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                  fontSize: "12px",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                +{Math.round(delta)}%
              </Box>
            )}
          </Box>

          {/* Progress bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                flex: 1,
                height: "6px",
                borderRadius: "9999px",
                backgroundColor: TRACK,
                overflow: "hidden",
              }}
            >
              <Box
                component={motion.div}
                initial={{ width: `${Math.max(0, pct - delta)}%` }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
                sx={{
                  height: "100%",
                  borderRadius: "9999px",
                  backgroundColor: GREEN,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "12px",
                color: GREEN,
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}
            >
              {pct}%
            </Typography>
          </Box>
        </Box>
      </SnackbarContent>
    );
  }
);

ProfileBoostSnackbar.displayName = "ProfileBoostSnackbar";

export default ProfileBoostSnackbar;
